import { ShadowRootTheGoodPart } from "lwc";

import { getNextFocusIndex, getFocusableElements } from "common/a11y";

export type MenuSelector = "trigger" | "menu" | "item";

export type MenuState = {
  count: number;
  open: boolean;
  selectedIndex: number;
  setup: boolean;
};

export function getSelector(selector: MenuSelector) {
  return `[data-menu="${selector}"]`;
}

const SELECTOR_TRIGGER = "trigger";
const SELECTOR_MENU = "menu";
const SELECTOR_ITEM = "item";

export const DEFAULT_MENU_STATE = {
  count: 0,
  open: false,
  selectedIndex: -1,
  setup: false,
};

export class Menu {
  private state: MenuState = { ...DEFAULT_MENU_STATE };

  private onChange: (state: MenuState) => void;
  private template: ShadowRootTheGoodPart;

  get itemCount() {
    return this.template.querySelectorAll(getSelector(SELECTOR_ITEM)).length;
  }

  constructor(
    template: ShadowRootTheGoodPart,
    onChange: (state: MenuState) => void
  ) {
    this.onChange = onChange;
    this.template = template;
    let $trigger = this.getElement(SELECTOR_TRIGGER)!;
    let $menu = this.getElement(SELECTOR_MENU)!;
    if ($trigger) {
      $trigger.addEventListener("mousedown", this.onTriggerMouseDown);
      $trigger.addEventListener("click", this.onTriggerClick);
      $trigger.addEventListener("keydown", this.onTriggerKeyDown);
    }

    if ($menu) {
      $menu.addEventListener("blur", this.onMenuBlur);
      $menu.addEventListener("click", this.onMenuClick);
      $menu.addEventListener("keydown", this.onMenuKeyDown);
    }

    this.setState({
      count: this.itemCount,
      setup: true,
    });
  }

  close() {
    this.setState({ open: false });
    this.template.host.dispatchEvent(new CustomEvent("requestclose"));
  }

  private getElement(selector: MenuSelector) {
    return this.template.querySelector(getSelector(selector));
  }

  getState() {
    return {
      ...this.state,
    };
  }

  open() {
    this.setState({
      open: true,
      selectedIndex: 0,
    });
    if (this.state.setup) {
      this.getElement(SELECTOR_MENU)!.focus();
    }
    this.template.host.dispatchEvent(new CustomEvent("requestopen"));
  }

  private onMenuClick = () => {
    this.close();
  };

  private onMenuBlur = () => {
    setTimeout(() => {
      let $active = this.template.activeElement;
      let $menu = this.getElement(SELECTOR_MENU)!;
      if ($menu === $active || $menu.contains($active)) return;
      this.close();
    });
  };

  private onMenuKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        this.setState({
          selectedIndex: getNextFocusIndex(
            "prev",
            this.state.selectedIndex,
            this.state.count
          ),
        });
        break;
      case "ArrowDown":
        e.preventDefault();
        this.setState({
          selectedIndex: getNextFocusIndex(
            "next",
            this.state.selectedIndex,
            this.state.count
          ),
        });
        break;
      case "Enter":
        e.preventDefault();
        if (!this.state.open) break;
        this.close();
        this.select();
        break;
      case "Escape":
      case "Esc": {
        e.preventDefault();
        this.close();
        let [element] = getFocusableElements(
          this.getElement(SELECTOR_TRIGGER)!
        );
        if (element) element.focus();
        break;
      }
      default:
        break;
    }
  };

  private onTriggerMouseDown = (e: MouseEvent) => {
    // HACK prevent mousedown from stealing focus onMenuBlur so
    // that menu can be closed on click
    e.preventDefault();
  };

  private onTriggerClick = () => {
    if (this.state.open) this.close();
    else this.open();
  };

  private onTriggerKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
      case "Space":
      case "ArrowDown":
        e.preventDefault();
        this.open();
        break;
      default:
        break;
    }
  };

  private select() {
    let element = this.template.querySelectorAll(getSelector(SELECTOR_ITEM))[
      this.state.selectedIndex
    ] as HTMLElement | undefined;
    if (element) element.click();
  }

  private setAttributes() {
    this.setTriggerAttributes();
    this.setMenuAttributes();
    this.setMenuItemsAttributes();
  }

  setState(
    nextState: Partial<MenuState> | ((state: MenuState) => Partial<MenuState>)
  ) {
    let { state } = this;
    this.state = {
      ...state,
      ...(typeof nextState === "function" ? nextState(state) : nextState),
    };
    this.setAttributes();
    this.onChange(this.state);
  }

  private setTriggerAttributes() {
    if (!this.state.setup) {
      return;
    }
    let $trigger = this.getElement(SELECTOR_TRIGGER)!;
    let $menu = this.getElement(SELECTOR_MENU)!;

    if (!$trigger) {
      return;
    }
    if (!$menu) {
      return;
    }
    $trigger.setAttribute("aria-controls", $menu.getAttribute("id") || "");
    $trigger.setAttribute("aria-expanded", String(this.state.open));
    $trigger.setAttribute("aria-haspopup", "true");
    $trigger.setAttribute("role", "button");
    $menu.setAttribute("role", "menu");
  }

  private setMenuAttributes() {
    if (!this.state.setup) {
      return;
    }

    let $menu = this.getElement(SELECTOR_MENU)!;
    if (!$menu) {
      return;
    }
    let $activeDecendant = this.template.querySelectorAll(
      getSelector(SELECTOR_ITEM)
    )[this.state.selectedIndex] as HTMLElement | undefined;
    $menu.setAttribute(
      "aria-activedescendant",
      $activeDecendant ? $activeDecendant.getAttribute("id") || "" : ""
    );
    $menu.setAttribute("aria-hidden", String(!this.state.open));
    $menu.style.zIndex = this.state.open ? "9999" : "";
    $menu.style.display = this.state.open ? "block" : "none";
    if (this.state.open) {
      $menu.setAttribute("tabindex", "0");
    } else {
      $menu.removeAttribute("tabindex");
    }
    this.template.querySelectorAll("ui,li").forEach((node) => {
      let element = node as HTMLElement;
      element.setAttribute("role", "presentation");
    });
    this.template
      .querySelectorAll(getSelector(SELECTOR_ITEM))
      .forEach((node) => {
        let element = node as HTMLElement;
        element.setAttribute("tabindex", "-1");
      });
  }

  private setMenuItemsAttributes() {
    this.template
      .querySelectorAll(getSelector(SELECTOR_ITEM))
      .forEach((node, i) => {
        let element = node as HTMLElement;
        element.setAttribute(
          "data-active",
          String(i === this.state.selectedIndex)
        );
        element.setAttribute("role", "menuitem");
      });
  }

  teardown() {
    let $trigger = this.getElement(SELECTOR_TRIGGER)!;
    let $menu = this.getElement(SELECTOR_MENU)!;
    if ($trigger) {
      $trigger.removeEventListener("click", this.onTriggerClick);
      $trigger.removeEventListener("keydown", this.onTriggerKeyDown);
    }
    if ($menu) {
      $menu.removeEventListener("blur", this.onMenuBlur);
      $menu.removeEventListener("click", this.onMenuClick);
      $menu.removeEventListener("keydown", this.onMenuKeyDown);
    }
  }
}
